import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../hooks/AuthContext';
import useAdminAutoRefresh from './useAdminAutoRefresh';
import './AdminDashboard.css';
import './AdminAnalytics.css';

const AUTO_REFRESH_INTERVAL_MS = 20000;

const RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'all', label: 'All Time' },
];

const endpointConfigs = [
  { key: 'overview', path: '/.netlify/functions/admin-get-analytics-overview' },
  { key: 'sessions', path: '/.netlify/functions/admin-get-session-analytics' },
  { key: 'users', path: '/.netlify/functions/admin-get-user-analytics' },
  { key: 'content', path: '/.netlify/functions/admin-get-content-analytics' },
  { key: 'credits', path: '/.netlify/functions/admin-get-credit-analytics' },
  { key: 'tokens', path: '/.netlify/functions/admin-get-token-analytics' },
  { key: 'errors', path: '/.netlify/functions/admin-get-error-analytics' },
];

function formatInteger(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value) || 0);
}

function formatDecimal(value, fractionDigits = 2) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Number(value) || 0);
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function formatMicroCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(Number(value) || 0);
}

function formatDuration(seconds) {
  const totalSeconds = Number(seconds) || 0;
  if (!totalSeconds) {
    return '0m';
  }

  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

function formatDateTime(value) {
  if (!value) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

const MetricCard = ({ title, value, helper }) => (
  <article className="cq-admin-metric-card">
    <p className="cq-admin-metric-card__title">{title}</p>
    <p className="cq-admin-metric-card__value">{value}</p>
    {helper ? <p className="cq-admin-metric-card__helper">{helper}</p> : null}
  </article>
);

const SectionCard = ({ title, subtitle, action, children }) => (
  <section className="cq-admin-summary-card">
    <div className="cq-admin-summary-card__header">
      <div>
        <h3>{title}</h3>
        {subtitle ? <p className="cq-admin-analytics-subtitle">{subtitle}</p> : null}
      </div>
      {action || null}
    </div>
    {children}
  </section>
);

const TrendBars = ({ items, formatter = formatInteger }) => {
  const maxValue = items.reduce((highest, item) => Math.max(highest, Number(item.value) || 0), 0);

  if (!items.length) {
    return <p className="cq-admin-empty-state__copy">No analytics data available.</p>;
  }

  return (
    <div className="cq-admin-analytics-bars">
      {items.map((item) => {
        const numericValue = Number(item.value) || 0;
        const width = maxValue > 0 ? Math.max((numericValue / maxValue) * 100, numericValue > 0 ? 4 : 0) : 0;
        return (
          <div key={item.date} className="cq-admin-analytics-bars__row">
            <span className="cq-admin-analytics-bars__label">{item.date}</span>
            <div className="cq-admin-analytics-bars__track">
              <span className="cq-admin-analytics-bars__fill" style={{ width: `${width}%` }} />
            </div>
            <span className="cq-admin-analytics-bars__value">{formatter(numericValue)}</span>
          </div>
        );
      })}
    </div>
  );
};

const RankedList = ({ items, valueFormatter = formatInteger, emptyLabel = 'No analytics data available.' }) => {
  if (!items.length) {
    return <p className="cq-admin-empty-state__copy">{emptyLabel}</p>;
  }

  return (
    <ul className="cq-admin-compact-list">
      {items.map((item) => (
        <li key={item.id || item.key || item.name}>
          <span>{item.name || item.key}</span>
          <span>{valueFormatter(item.sessions ?? item.count ?? item.cost ?? item.value)}</span>
        </li>
      ))}
    </ul>
  );
};

const AdminAnalyticsPage = () => {
  const { authenticatedUser } = useAuthContext();
  const [range, setRange] = useState('7d');
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    error: '',
    data: null,
    lastUpdatedAt: null,
  });
  const { refreshNonce, triggerRefresh } = useAdminAutoRefresh({
    enabled: Boolean(authenticatedUser?.token),
    intervalMs: AUTO_REFRESH_INTERVAL_MS,
  });

  useEffect(() => {
    if (!authenticatedUser?.token) {
      return undefined;
    }

    const controller = new AbortController();

    const loadAnalytics = async ({ background = false } = {}) => {
      setState((current) => ({
        ...current,
        loading: background ? current.loading : true,
        refreshing: background,
        error: '',
      }));

      try {
        const responses = await Promise.all(endpointConfigs.map(async (endpoint) => {
          const response = await fetch(endpoint.path, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authenticatedUser.token}`,
            },
            body: JSON.stringify({ range }),
            signal: controller.signal,
          });

          const payload = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(payload?.message || `Unable to load ${endpoint.key} analytics.`);
          }

          return [endpoint.key, payload];
        }));

        setState({
          loading: false,
          refreshing: false,
          error: '',
          data: Object.fromEntries(responses),
          lastUpdatedAt: new Date().toISOString(),
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setState({
          loading: false,
          refreshing: false,
          error: error.message || 'Unable to load analytics.',
          data: null,
          lastUpdatedAt: null,
        });
      }
    };

    loadAnalytics({ background: refreshNonce > 0 });

    return () => controller.abort();
  }, [authenticatedUser, range, refreshNonce]);

  const analytics = state.data;
  const overview = analytics?.overview?.overview;
  const sessionAnalytics = analytics?.sessions?.sessions;
  const userAnalytics = analytics?.users?.users;
  const contentAnalytics = analytics?.content?.content;
  const creditAnalytics = analytics?.credits?.credits;
  const tokenAnalytics = analytics?.tokens?.tokens;
  const errorAnalytics = analytics?.errors?.errors;

  const hasAnyData = useMemo(() => {
    return Boolean(
      overview?.total_sessions
      || overview?.active_users
      || tokenAnalytics?.total_tokens
      || errorAnalytics?.total_errors
      || contentAnalytics?.game_packs?.length
    );
  }, [contentAnalytics, errorAnalytics, overview, tokenAnalytics]);

  return (
    <section className="cq-admin-page">
      <header className="cq-admin-page__header">
        <div>
          <p className="cq-admin-page__eyebrow">Decision Tool</p>
          <h2 className="cq-admin-page__title">Analytics</h2>
          <p className="cq-admin-page__copy">Focused operational insight into sessions, users, content, credits, cost, and stability.</p>
        </div>

        <div className="cq-admin-page__actions">
          <div className="cq-admin-page__actions-row cq-admin-range-filter" role="tablist" aria-label="Analytics range">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`cq-admin-range-filter__button${range === option.value ? ' is-active' : ''}`}
                onClick={() => setRange(option.value)}
              >
                {option.label}
              </button>
            ))}
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.loading || state.refreshing}
              onClick={triggerRefresh}
            >
              Refresh
            </button>
          </div>
          <p className="cq-admin-page__meta">
            {state.refreshing ? 'Refreshing...' : `Last updated ${formatDateTime(state.lastUpdatedAt)}`}
          </p>
        </div>
      </header>

      {state.loading ? (
        <div className="cq-admin-empty-state">
          <p className="cq-admin-empty-state__title">Loading analytics...</p>
        </div>
      ) : null}

      {!state.loading && state.error ? (
        <div className="cq-admin-empty-state">
          <p className="cq-admin-empty-state__title">Unable to load analytics.</p>
          <p className="cq-admin-empty-state__copy">{state.error}</p>
        </div>
      ) : null}

      {!state.loading && !state.error && !hasAnyData ? (
        <div className="cq-admin-empty-state">
          <p className="cq-admin-empty-state__title">No analytics data available.</p>
        </div>
      ) : null}

      {!state.loading && !state.error && hasAnyData ? (
        <>
          <div className="cq-admin-metrics-grid">
            <MetricCard title="Total Sessions" value={formatInteger(overview?.total_sessions)} helper="Selected range" />
            <MetricCard title="Completed Sessions" value={formatInteger(overview?.completed_sessions)} helper={`${formatDecimal(sessionAnalytics?.completion_rate || 0, 1)}% completion`} />
            <MetricCard title="Failed Sessions" value={formatInteger(overview?.failed_sessions)} helper={`${formatDecimal(sessionAnalytics?.failure_rate || 0, 1)}% failure`} />
            <MetricCard title="Active Users" value={formatInteger(overview?.active_users)} helper={`${formatInteger(overview?.returning_users)} returning`} />
            <MetricCard title="New Users" value={formatInteger(overview?.new_users)} helper={`${formatInteger(overview?.returning_users)} returning`} />
            <MetricCard title="Avg Session Duration" value={formatDuration(overview?.average_session_duration_seconds)} helper="Completed sessions" />
            <MetricCard title="Avg Credits / Session" value={formatDecimal(overview?.average_credits_used_per_session)} helper="Charged sessions mixed into global DB totals" />
            <MetricCard title="Total Tokens Used" value={formatInteger(overview?.total_tokens_used)} helper={`Est. cost ${formatCurrency(overview?.estimated_cost)}`} />
          </div>

          <div className="cq-admin-analytics-grid">
            <SectionCard title="Sessions" subtitle="Volume and session quality over time." action={<Link className="cq-admin-card-link" to="/admin/sessions">Open Sessions</Link>}>
              <div className="cq-admin-analytics-stat-grid">
                <div><dt>Completion Rate</dt><dd>{formatDecimal(sessionAnalytics?.completion_rate || 0, 1)}%</dd></div>
                <div><dt>Failure Rate</dt><dd>{formatDecimal(sessionAnalytics?.failure_rate || 0, 1)}%</dd></div>
                <div><dt>Avg Duration</dt><dd>{formatDuration(sessionAnalytics?.average_session_duration_seconds)}</dd></div>
              </div>
              <div className="cq-admin-summary-subsection">
                <p className="cq-admin-summary-subsection__title">Sessions Per Day</p>
                <TrendBars items={sessionAnalytics?.sessions_per_day || []} />
              </div>
            </SectionCard>

            <SectionCard title="Users" subtitle="Engagement and returning behavior.">
              <div className="cq-admin-analytics-stat-grid">
                <div><dt>Active Users</dt><dd>{formatInteger(userAnalytics?.active_users)}</dd></div>
                <div><dt>New Users</dt><dd>{formatInteger(userAnalytics?.new_users)}</dd></div>
                <div><dt>Returning Users</dt><dd>{formatInteger(userAnalytics?.returning_users)}</dd></div>
                <div><dt>Sessions / User</dt><dd>{formatDecimal(userAnalytics?.average_sessions_per_user)}</dd></div>
              </div>
              <div className="cq-admin-summary-subsection">
                <p className="cq-admin-summary-subsection__title">Active Users Per Day</p>
                <TrendBars items={userAnalytics?.active_users_per_day || []} />
              </div>
            </SectionCard>

            <SectionCard title="Content Usage" subtitle="What players are actually touching." action={<Link className="cq-admin-card-link" to="/admin/content">Open Content</Link>}>
              <div className="cq-admin-analytics-rank-grid">
                <div>
                  <p className="cq-admin-summary-subsection__title">Top Game Packs</p>
                  <RankedList items={contentAnalytics?.game_packs || []} />
                </div>
                <div>
                  <p className="cq-admin-summary-subsection__title">Top Hosts</p>
                  <RankedList items={contentAnalytics?.hosts || []} />
                </div>
                <div>
                  <p className="cq-admin-summary-subsection__title">Top Game Modes</p>
                  <RankedList items={contentAnalytics?.game_modes || []} />
                </div>
                <div>
                  <p className="cq-admin-summary-subsection__title">Quest Packs</p>
                  <RankedList items={contentAnalytics?.quest_packs || []} />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Credits" subtitle="Credit flow, consumption, and session burn." action={<Link className="cq-admin-card-link" to="/admin/payments">Open Payments</Link>}>
              <div className="cq-admin-analytics-stat-grid">
                <div><dt>Avg Credits / Session</dt><dd>{formatDecimal(creditAnalytics?.average_credits_per_session)}</dd></div>
              </div>
              <div className="cq-admin-analytics-rank-grid">
                <div>
                  <p className="cq-admin-summary-subsection__title">Credits Purchased Per Day</p>
                  <TrendBars items={creditAnalytics?.credits_purchased_per_day || []} />
                </div>
                <div>
                  <p className="cq-admin-summary-subsection__title">Credits Consumed Per Day</p>
                  <TrendBars items={creditAnalytics?.credits_consumed_per_day || []} />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Tokens & Cost" subtitle="LLM usage and cost drivers from conversation logs.">
              <div className="cq-admin-analytics-stat-grid">
                <div><dt>Total Tokens</dt><dd>{formatInteger(tokenAnalytics?.total_tokens)}</dd></div>
                <div><dt>Total Cost</dt><dd>{formatCurrency(tokenAnalytics?.total_cost)}</dd></div>
                <div><dt>Tokens / Session</dt><dd>{formatInteger(tokenAnalytics?.average_tokens_per_session)}</dd></div>
                <div><dt>Cost / Session</dt><dd>{formatMicroCurrency(tokenAnalytics?.average_cost_per_session)}</dd></div>
              </div>
              <div className="cq-admin-analytics-rank-grid">
                <div>
                  <p className="cq-admin-summary-subsection__title">Token Usage Per Day</p>
                  <TrendBars items={tokenAnalytics?.token_usage_per_day || []} />
                </div>
                <div>
                  <p className="cq-admin-summary-subsection__title">Cost Per Day</p>
                  <TrendBars items={tokenAnalytics?.cost_per_day || []} formatter={formatCurrency} />
                </div>
                <div>
                  <p className="cq-admin-summary-subsection__title">Highest-Cost Sessions</p>
                  <RankedList
                    items={tokenAnalytics?.top_cost_sessions || []}
                    valueFormatter={formatMicroCurrency}
                    emptyLabel="No session cost data available."
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Errors" subtitle="Stability signals by severity and layer." action={<Link className="cq-admin-card-link" to="/admin/sessions/errors">Open Errors</Link>}>
              <div className="cq-admin-analytics-stat-grid">
                <div><dt>Total Errors</dt><dd>{formatInteger(errorAnalytics?.total_errors)}</dd></div>
              </div>
              <div className="cq-admin-analytics-rank-grid">
                <div>
                  <p className="cq-admin-summary-subsection__title">Errors Per Day</p>
                  <TrendBars items={errorAnalytics?.errors_per_day || []} />
                </div>
                <div>
                  <p className="cq-admin-summary-subsection__title">By Severity</p>
                  <RankedList items={errorAnalytics?.by_severity || []} />
                </div>
                <div>
                  <p className="cq-admin-summary-subsection__title">By Layer</p>
                  <RankedList items={errorAnalytics?.by_layer || []} />
                </div>
              </div>
            </SectionCard>
          </div>

          <p className="cq-admin-page__meta cq-admin-analytics-footnote">
            Session, user, and content analytics reflect the shared database and are not environment-filtered in this version.
          </p>
        </>
      ) : null}
    </section>
  );
};

export default AdminAnalyticsPage;
